import Utils from "../../utils";

export default {
    namespaced: true,

    state: {

        GRID_CELL_SIZE: 10,  // 10px grid cell size. (3 cells in 1 foot)
        pixelToInch: (4/10), // 4 inch in the 10 pixels grid cell (GRID_CELL_SIZE), because we use 10 pixel step in the Background grid
        pixelToInchForSmallTrim: (1/10), // 1 inch in the 10 pixels grid cell for the Small trim mode
        footFractionsNumber: 16,

        stageWidth: 500,
        stageHeight: 500,
        infoBoxWidth: 250,

        // USER_ACTIONS:
        // 'SELECT' - user select line or angle (any shape for edit) on canvas
        // 'DRAW_LINE' - user drawing lines
        // 'SET_OPEN_HEM', 'SET_CLOSE_HEM' - user set open/close on the ends of the
        // 'SET_FOLD' - user set a small line perpendicularly to the current. this line has no size and paint
        USER_ACTIONS: {
            SELECT: 'SELECT',
            DRAW_LINE: 'DRAW_LINE',
            SET_OPEN_HEM: 'SET_OPEN_HEM',
            SET_CLOSE_HEM: 'SET_CLOSE_HEM',
            SET_FOLD: 'SET_FOLD',
        },
        userAction: 'DRAW_LINE',

        points: [],

        drawingState: false, // status shows whether the line is currently drawn

        snapToGrid: false,
        gridDisplay: true,
        ZOOM_MIN: 1,
        ZOOM_MAX: 2,
        zoom: 1,
        zoomStep: 0.25,

        canvasStage: null,
        isFontsLoaded: false,
    },

    getters: {
        trimEditDisabled(state, getters, rootState, rootGetters) {
            const hasUserAccess = rootGetters['authentication/hasUserAccess'];
            const isTrimExported = rootState['trimInfo'].isTrimExported;
            const permissionToEdit = rootState['trimInfo'].permissionToEdit;
            
            const disabled = !hasUserAccess || isTrimExported || !permissionToEdit;
            
            console.log('trimEditDisabled check:', {
                hasUserAccess,
                isTrimExported,
                permissionToEdit,
                disabled
            });
            
            return disabled;
        },

        footToPx(state, getters, rootState) {
            let pixelToInch = rootState['trimInfo'].isSmallTrim ?
                state.pixelToInchForSmallTrim :
                state.pixelToInch;

            return 12 / pixelToInch;
        },

        getFootsInFractions:(state) => (lengthInFoots) => {
            if (!lengthInFoots) return '0';

            const footFractionsNumber = state.footFractionsNumber;

            let integralPart = Math.floor(lengthInFoots); // 16
            let fractionalPart = Math.round(footFractionsNumber * (lengthInFoots - integralPart));
            let fractionalPartText = '';

            if (fractionalPart === 16) { // footFractionsNumber
                integralPart += 1;
                fractionalPart = 0;
            }
            else if (fractionalPart === 14) {
                fractionalPartText = '7/8';
            }
            else if (fractionalPart === 12) {
                fractionalPartText = '3/4';
            }
            else if (fractionalPart === 10) {
                fractionalPartText = '5/8';
            }
            else if (fractionalPart === 8) {
                fractionalPartText = '1/2';
            }
            else if (fractionalPart === 6) {
                fractionalPartText = '3/8';
            }
            else if (fractionalPart === 4) {
                fractionalPartText = '1/4';
            }
            else if (fractionalPart === 2) {
                fractionalPartText = '1/8';
            }
            else {
                fractionalPartText = `${fractionalPart}/16`; // footFractionsNumber
            }

            let footsInFractions = '';

            if (!integralPart) {
                footsInFractions = fractionalPartText;
            }
            else if (!fractionalPart) {
                footsInFractions = `${integralPart}`;
            }
            else {
                footsInFractions = `${integralPart}-${fractionalPartText}`;
            }

            return footsInFractions;
        },

        fractionsDecimalRound: (state) => (number) => {
            const footFractionsNumber = state.footFractionsNumber;
            let integralPart = Math.floor(number);

            return integralPart + Math.round((number - integralPart) * footFractionsNumber) / footFractionsNumber;
        },

        getPointById: (state) => (id) => {
            return state.points.find(point => +point.id === +id);
        },

        getPointIndexById: (state) => (id) => {
            return state.points.findIndex(point => +point.id === +id);
        },

        endPointers(state) {
            const points = state.points;
            const pointsNumber = points.length;

            let pointers = [];

            if (pointsNumber > 0) {
                pointers.push(points[0]);
            }

            if (pointsNumber > 1) {
                pointers.push(points[pointsNumber - 1]);
            }

            return pointers;
        },

        hems(state, getters) {
            const endPointers = getters.endPointers;

            if (!endPointers.length) return [];

            let hems = [];

            hems.push(endPointers[0].hem || null);
            hems.push(endPointers[1].hem || null);

            return hems;
        },

        hemPreviousPoint: (state, getters) => (id) => {
            let connectionPointIndex = +getters.getPointIndexById(id);
            let prevPoint;

            if (connectionPointIndex === 0) {
                // if pointer is the first point
                // set the next one to the prevPoint
                prevPoint = state.points[1];
            }
            else {
                // else pointer is the last point
                // set the previous one to the prevPoint
                prevPoint = state.points[connectionPointIndex - 1];
            }

            return prevPoint;
        },

        disableHems(state, getters) {
            return !state.points.length || getters.trimIsCompleted || getters.trimEditDisabled;
        },

        isTrimExists(state) {
            return !!state.points.length;
        },

        trimIsCompleted(state, getters) {
            if (!getters.isTrimExists) return false;

            // check if both hems are placed
            const points = state.points;
            return points[0].hem && points[points.length - 1].hem;
        },

        trimTotalLength(state, getters, rootState, rootGetters) {
            let totalLength = 0;
            let lines = rootGetters['line/getLinesWithoutFolds'](state.points);
            let hems = getters.hems;

            // sum all lines length
            lines.forEach(line => {
                let type = line.type;
                let length = 0;

                if (type === "lineWithRibs") {
                    // for line with ribs use Starting Length
                    length = rootGetters['line/getLineWithRibsStartingLength'](line);
                }
                else {
                    length = line.lengthInFeet;
                }

                totalLength += getters.fractionsDecimalRound(length);
            });

            hems.forEach(hem => {
                if (!hem) return;

                totalLength += getters.fractionsDecimalRound(hem.size);
            });

            return getters.getFootsInFractions(totalLength);
        },

        snapPositionToGrid: (state) => (position) => {
            if (!state.snapToGrid) return position;

            const cellSize = state.GRID_CELL_SIZE;

            let x = Math.round(position.x / cellSize) * cellSize;
            let y = Math.round(position.y / cellSize) * cellSize;

            return { x, y };
        },
    },

    mutations: {
        setCanvasStage(state, canvasStage) {
            state.canvasStage = canvasStage;
        },

        setCanvasStageSize(state, stageSize) {
            state.stageWidth = +stageSize.width;
            state.stageHeight = +stageSize.height;
        },

        setUserAction(state, userAction) {
            state.userAction = userAction;
        },

        setDrawingState(state, drawingState) {
            state.drawingState = drawingState;
        },

        setPoints(state, points) {
            state.points = points;
        },

        setHem(state, {point, hem}) {
            point['hem'] = hem;
        },

        setHemSize(state, {point, size}) {
            point.hem['size'] = size;
        },

        setHemFlip(state, {point, flip}) {
            point.hem['flip'] = flip;
        },

        setHemIsHover(state, { point, isHover }) {
            point.hem['isHover'] = isHover;
        },

        setSnapToGrid(state, snapState) {
            state.snapToGrid = snapState;

            // save this state to the localStorage to avoid the toggling an each time
            localStorage.setItem('snapToGrid', snapState);
        },

        setGridDisplay(state, display) {
            state.gridDisplay = display;

            // save this state to the localStorage to avoid the toggling an each time
            localStorage.setItem('gridDisplay', display);
        },

        setZoom(state, zoom) {
            state.zoom = zoom;
        },

        setIsFontsLoaded(state) {
            state.isFontsLoaded = true;
        },
    },

    actions: {

        stopDrawing({state, commit}) {
            if (state.drawingState) {

                commit('setDrawingState', false);
            }
        },

        clickedOnCanvas({state, getters, dispatch}, { pointerPosition, shape }) {

            // we need to get the position where the user had clicked
            const clickPosition = getters.snapPositionToGrid(pointerPosition);

            // check what the user action was selected
            switch (state.userAction) {

                // the user can select an element to modify its properties
                case state.USER_ACTIONS.SELECT: {
                    break;
                }

                case state.USER_ACTIONS.DRAW_LINE: {
                    const drawingState = state.drawingState;

                    // on click we set start or end point of the line
                    dispatch('line/drawLineUserAction', { clickPosition, shape, drawingState }, {root: true});

                    break;
                }

                case state.USER_ACTIONS.SET_OPEN_HEM:
                case state.USER_ACTIONS.SET_CLOSE_HEM:
                case state.USER_ACTIONS.SET_FOLD: {

                    dispatch('backUserToDrawAction');

                    break;
                }
            }
        },

        backUserToDrawAction({state, getters, commit}) {
            let nextAction;

            // check if both hems are placed
            if (getters.trimIsCompleted) {
                nextAction = state.USER_ACTIONS.SELECT;
            }
            else {
                nextAction = state.USER_ACTIONS.DRAW_LINE;
            }

            commit('setUserAction', nextAction);
        },

        setPoints({commit}, points) {
            commit('setPoints', points);
        },

        addPointToStart({state, commit, dispatch}, point) {
            let points = state.points;
            let firstPoint = points[0];

            // transfer the paintEnabled option from the first trim point to the new one
            commit('paint/setPaintEnabled', { point: point, enabled: firstPoint['paintEnabled'] }, {root: true});

            // remove the paintEnabled option from the previous point
            delete firstPoint['paintEnabled'];

            points.unshift(point);

            dispatch('setPoints', points);
        },

        addPointToEnd({state, dispatch}, point) {
            let points = state.points;

            points.push(point);

            dispatch('setPoints', points);
        },

        addHem({state, getters, commit}, { pointId }) {
            const id = pointId;

            let point = getters.getPointById(pointId);

            let type = state.userAction === state.USER_ACTIONS.SET_OPEN_HEM ? 'openHem' : 'closeHem';

            let hem = {
                id,
                type,
                size: 0.5,
                flip: false,
                isHover: false,
            };

            commit('setHem', { point, hem });
        },

        setHemSize({getters, commit}, {point, size}) {
            let sizeRound = getters.fractionsDecimalRound(size);

            commit('setHemSize', {point, size: sizeRound})
        },

        async addFold({state, getters, rootGetters, commit, dispatch}, { pointId }) {

            // stop the undo-redo history change
            // due to making many consecutive changes to one variable
            await dispatch('undoRedo/stopAddingToHistory', null, {root: true});

            const foldSize = state.GRID_CELL_SIZE;

            let currentPoint = getters.getPointById(pointId);
            let prevPoint = getters.hemPreviousPoint(pointId);
            let sideCoefficient = 1; // just 1 (use in paint)

            const currentPointIndex = getters.getPointIndexById(pointId);

            // get the trim direction
            const isAddFoldToTrimStart = currentPointIndex === 0;

            // use in case if the fold is adding to the start of the trim
            const paintEnabledStatus = !!currentPoint['paintEnabled'];

            // set the "start" point of the fold line to be able to flip the following lines around it
            commit('line/setIsFoldStart', { point: currentPoint, isFoldStart: true }, {root: true});


            // get position for the new 'toPoint' at 90deg to the previous line
            let newPoint = rootGetters['paint/pointOnEdge'](currentPoint, prevPoint, sideCoefficient, foldSize);

            let fold = {
                fromPoint: currentPoint,
                toPoint: newPoint,
            };

            // the fold adding (set "id" to the line)
            let newPointResult = await dispatch('line/addLine', fold, {root: true});

            // set the fold type to the line in correct direction
            let foldStartPoint = isAddFoldToTrimStart ? newPointResult : currentPoint;

            // and set a "fold" type to the point on the "fold line" that comes first in the order of the trim points
            commit('line/setLineType', { point: foldStartPoint, type: 'fold' }, {root: true});


            // add a second line that folds from the previous line before the fold
            let dx = newPoint.x - currentPoint.x;
            let dy = newPoint.y - currentPoint.y;

            let secondLineToPoint = {
                x: prevPoint.x + dx,
                y: prevPoint.y + dy,
            };

            let secondLine = {
                fromPoint: newPointResult,
                toPoint: secondLineToPoint,
            };

            // the parallel line adding
            let parallelLineEndPoint = await dispatch('line/addLine', secondLine, {root: true});

            // set "paintEnabled"
            if (isAddFoldToTrimStart) {

                // add new paint part to the trim new start point
                commit('paint/setPaintEnabled', { point: parallelLineEndPoint, enabled: true }, {root: true});

                // because the "addLine" will transfer paintEnabled Status to the next added point
                // is need back the paintEnabled Status to the previous start point of the trim
                commit('paint/setPaintEnabled', { point: currentPoint, enabled: paintEnabledStatus }, {root: true});
            }
            else {

                // set paintEnabled to the next part after the fold
                commit('paint/setPaintEnabled', { point: newPointResult, enabled: true }, {root: true});
            }

            // for update the paintEnabled options on the "paint parts" we need to update the "points"
            let updatePoints = Utils.deepCopy(state.points);

            // recover the undo-redo history change
            await dispatch('undoRedo/recoverAddingToHistory', null, {root: true});

            dispatch('setPoints', updatePoints);
        },

        clearTrim({state, commit, dispatch}) {
            dispatch('setPoints', []);

            commit('setUserAction', state.USER_ACTIONS.DRAW_LINE);
        },

        setZoom({state, commit}, zoom) {
            let newZoom = Math.max(state.ZOOM_MIN, Math.min(state.ZOOM_MAX, +zoom));

            commit('setZoom', newZoom);
        },

        centerTrimOnCanvas({state, getters, dispatch}) {
            if(!getters.isTrimExists) return;

            let getLimits = function(points) {
                let x = points[0].x;
                let y = points[0].y;

                let xMin = x;
                let xMax = x;
                let yMin = y;
                let yMax = y;

                for (let i = 1; i < points.length; i++) {
                    x = points[i].x;
                    y = points[i].y;

                    xMin = Math.min(xMin, x);
                    xMax = Math.max(xMax, x);
                    yMin = Math.min(yMin, y);
                    yMax = Math.max(yMax, y);
                }

                return { xMin, xMax, yMin, yMax };
            };

            let limits = getLimits(state.points);
            let infoBoxWidth = state.infoBoxWidth;

            let trimWidth  = limits.xMax - limits.xMin;
            let trimHeight = limits.yMax - limits.yMin;

            let left = infoBoxWidth/2 + (state.stageWidth - trimWidth) / 2;
            let top = (state.stageHeight - trimHeight) / 2;

            let dx = left - limits.xMin;
            let dy = top - limits.yMin;

            let newPoints = Utils.deepCopy(state.points);

            newPoints.forEach(point => {
                point.x += dx;
                point.y += dy;
            });

            dispatch('setPoints', newPoints);
        },

        hoverTrimItem({getters, commit}, {isHover, itemType, pointId}) {

            let point = getters.getPointById(pointId);

            switch (itemType) {
                case 'line':
                case 'lineWithRibs':

                    commit('line/setLineIsHover', { point, isHover }, {root: true});

                    break;

                case 'hem':

                    commit('setHemIsHover', { point, isHover });

                    break;

                case 'angel':
                    commit('angles/setAngleIsHover', { point, isHover }, {root: true});


                    break;

                default:
                    break;
            }
        },

        resetStatesToInitialState({state, commit, dispatch}) {
            commit('setUserAction', state.USER_ACTIONS.DRAW_LINE);
            commit('setDrawingState', false);
            dispatch('setPoints', []);
            dispatch('editPopup/clearCurrentPopup', null, {root: true});
            commit('setZoom', 1);

            let snapToGrid = JSON.parse(localStorage.getItem('snapToGrid'));
            let gridDisplay = JSON.parse(localStorage.getItem('gridDisplay'));

            commit('setSnapToGrid', snapToGrid);
            commit('setGridDisplay', gridDisplay);
        },
    },

}